import * as Crypto from 'expo-crypto';
import {
  CalendarCheck2,
  Check,
  type LucideIcon,
  MessageSquareText,
  RefreshCw,
  ShoppingCart,
  Sparkles,
  UserRoundSearch,
} from 'lucide-react-native';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Switch,
  type TextInput,
  useWindowDimensions,
  View,
} from 'react-native';
import Animated, { ReduceMotion, SlideInRight, SlideOutLeft } from 'react-native-reanimated';
import { Button } from '../../../../components/primitives/Button';
import { DatePickerInput } from '../../../../components/primitives/DatePickerInput';
import { FormInput } from '../../../../components/primitives/FormInput';
import { HStack, VStack } from '../../../../components/primitives/Stack';
import { Body, Caption, Heading3, Heading4, Label } from '../../../../components/primitives/Text';
import { TimePickerInput } from '../../../../components/primitives/TimePickerInput';
import { useToast } from '../../../../contexts/ToastContext';
import { useTranslation } from '../../../../contexts/TranslationContext';
import { trackEvent } from '../../../../lib/analytics';
import { useMixpanel } from '../../../../lib/mixpanel';
import { colors } from '../../../../theme/colors';
import { LANGUAGE_OPTION_VALUES, USE_CASE_OPTION_VALUES } from '../../constants';
import {
  useCreateVoiceAgent,
  usePublishVoiceAgent,
  useRenameVoiceAgent,
  useUpdateVoiceAgent,
  useUploadKnowledgeItem,
  useVoiceAgentScriptVariables,
  useVoiceAgentTemplates,
  useVoiceLibrary,
} from '../../hooks';
import { getOnboardingVideoUrl } from '../../lib/onboarding-videos';
import type {
  CallingHours,
  CreateVoiceAgentDto,
  DisclosurePolicy,
  KnowledgeBaseItem,
  RetryPolicy,
  ScriptTemplate,
  UpdateVoiceAgentDto,
  UseCaseTemplate,
  VoiceAgent,
  VoiceAgentLanguage,
  VoiceAgentUseCase,
} from '../../types';
import { KnowledgeManager } from '../KnowledgeManager';
import { ScriptVariableBuilder } from '../ScriptVariableBuilder';
import { VoicePreviewButton } from '../VoicePreviewButton';

const WEEK_DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
type WeekDay = (typeof WEEK_DAYS)[number];

const DAY_SHORT_LABELS: Record<WeekDay, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
};

function buildDefaultCallingDays(): Record<
  WeekDay,
  { enabled: boolean; start: string; end: string }
> {
  const result = {} as Record<WeekDay, { enabled: boolean; start: string; end: string }>;
  for (const d of WEEK_DAYS) {
    result[d] = {
      enabled: ['mon', 'tue', 'wed', 'thu', 'fri'].includes(d),
      start: '09:00',
      end: '18:00',
    };
  }
  return result;
}

/**
 * Where to reopen a draft (FLOSEAI-655).
 *
 * Resolves the recorded step NAME against the list actually being rendered.
 * Names rather than positions because the two step lists share only their first
 * three entries: "Knowledge" is the 4th onboarding step and the 5th full step,
 * and an abandoned onboarding draft is reopened through the normal Edit screen,
 * which renders the full flow. A recorded index would have resumed it on the
 * wrong step; a name lands on the right one, and resolves to nothing when that
 * step does not exist in this variant — in which case we start at the
 * beginning rather than guess.
 *
 * Only drafts resume. A published agent is complete, so it opens at the start
 * with the whole flow available, which is what it did before this existed.
 */
export function resolveResumeStepIndex(
  agent: { status?: string; draftStep?: string | null } | undefined,
  steps: readonly string[],
): number {
  if (!agent || agent.status !== 'draft') return 0;

  const recorded = typeof agent.draftStep === 'string' ? agent.draftStep.trim() : '';
  if (!recorded) return 0;

  const index = steps.indexOf(recorded);
  return index >= 0 ? index : 0;
}

const FULL_STEPS = [
  'Use Case',
  'Identity',
  'Voice',
  'Script',
  'Knowledge',
  'Objections',
  'Tools & Compliance',
  'Calling Hours',
  'Retry Policy',
  'Review & Publish',
] as const;

// Onboarding mode (H12) hides the deep-configuration steps so a new admin
// can publish in 5 clicks. Defaults from the chosen H9 template fill in
// what onboarding skips. The KB step (H12b) is optional — the admin can
// hit "Skip for now" and add documents later from the agent detail view.
const ONBOARDING_STEPS = [
  'Use Case',
  'Identity',
  'Voice',
  'Knowledge',
  'Calling Hours',
  'Review & Publish',
] as const;

const STEP_ENTERING = SlideInRight.duration(220).reduceMotion(ReduceMotion.System);
const STEP_EXITING = SlideOutLeft.duration(180).reduceMotion(ReduceMotion.System);

type WizardStep = (typeof FULL_STEPS)[number];

interface WizardState {
  name: string;
  description: string;
  useCase: VoiceAgentUseCase;
  language: VoiceAgentLanguage;
  tone: string;
  voiceId: string;
  scriptOpening: string;
  scriptCta: string;
  scriptClosing: string;
  // FLOSEAI-124 — QA reported the wizard offering no multi-language option.
  // There was one, spelled "ES (LATAM)", because that value doubled as the
  // sentinel for "work out the language from the lead". Making it its own
  // toggle is the only way the choice reads as a choice.
  autoDetectLanguage: boolean;
  alwaysDisclose: boolean;
  recordingAnnouncement: boolean;
  disclosureWording: string;
  useLeadTimezone: boolean;
  callingDays: Record<WeekDay, { enabled: boolean; start: string; end: string }>;
  blackoutDates: string[];
  retryMaxAttempts: string;
  kbSeedText: string;
  enabledTools: string[];
}

type TranslateFn = (key: string) => string;

const TONE_OPTION_VALUES: string[] = [
  'friendly_formal',
  'professional',
  'empathetic',
  'energetic',
  'neutral',
];

// Kept in sync with backend-nest ALLOWED_TOOLS
// (src/voice-agents/services/template-catalog.service.spec.ts).
const ALL_TOOL_VALUES: string[] = [
  'schedule_meeting',
  'transfer_to_human',
  'mark_do_not_call',
  'update_lead_qualification',
  'increment_objection_counter',
  'finalize_call',
  'lookup_knowledge_base',
];

const useCaseOptions = (t: TranslateFn): Array<{ value: VoiceAgentUseCase; label: string }> =>
  USE_CASE_OPTION_VALUES.map((value) => ({
    value,
    label: t(`voiceAgents.wizard.useCase.${value}`),
  }));

const languageOptions = (t: TranslateFn): Array<{ value: VoiceAgentLanguage; label: string }> =>
  LANGUAGE_OPTION_VALUES.map((value) => ({
    value,
    label: t(`voiceAgents.wizard.language.${value}`),
  }));

const toneOptions = (t: TranslateFn): Array<{ value: string; label: string }> =>
  TONE_OPTION_VALUES.map((value) => ({
    value,
    label: t(`voiceAgents.wizard.tone.${value}`),
  }));

const stepLabel = (t: TranslateFn, step: WizardStep): string =>
  t(`voiceAgents.wizard.steps.${STEP_SLUGS[step]}`);

const STEP_SLUGS: Record<WizardStep, string> = {
  'Use Case': 'useCase',
  Identity: 'identity',
  Voice: 'voice',
  Script: 'script',
  Knowledge: 'knowledge',
  Objections: 'objections',
  'Tools & Compliance': 'toolsCompliance',
  'Calling Hours': 'callingHours',
  'Retry Policy': 'retryPolicy',
  'Review & Publish': 'reviewPublish',
};

const USE_CASE_ICONS: Record<VoiceAgentUseCase, LucideIcon> = {
  initial_qualification: UserRoundSearch,
  warm_recontact: RefreshCw,
  meeting_confirmation: CalendarCheck2,
  cart_recovery: ShoppingCart,
  nps_survey: MessageSquareText,
  custom: Sparkles,
};

function buildDtoFromState(state: WizardState): CreateVoiceAgentDto {
  const scriptTemplate: ScriptTemplate = {
    opening: state.scriptOpening,
    cta: state.scriptCta,
    closing: state.scriptClosing,
  };

  const autoDetectLanguage = state.autoDetectLanguage;

  const disclosurePolicy: DisclosurePolicy = {
    alwaysDisclose: state.alwaysDisclose,
    recordingAnnouncement: state.recordingAnnouncement,
    wording: state.disclosureWording || undefined,
  };

  const windows = WEEK_DAYS.filter((d) => state.callingDays[d].enabled).map((d) => ({
    day: d,
    start: state.callingDays[d].start,
    end: state.callingDays[d].end,
  }));
  const callingHours: CallingHours = {
    useLeadTimezone: state.useLeadTimezone,
    fallbackTimezone: 'America/Bogota',
    windows,
    blackoutDates: state.blackoutDates,
  };

  const maxAttempts = Number.parseInt(state.retryMaxAttempts, 10);
  const retryPolicy: RetryPolicy = {
    maxAttempts: Number.isNaN(maxAttempts) ? 3 : Math.min(Math.max(maxAttempts, 1), 5),
    backoffMinutes: [30, 120, 480],
    stopOn: ['qualified', 'meeting_booked', 'do_not_call'],
  };

  return {
    name: state.name,
    description: state.description || undefined,
    useCase: state.useCase,
    language: state.language,
    autoDetectLanguage,
    tone: state.tone,
    voiceId: state.voiceId,
    scriptTemplate,
    disclosurePolicy,
    callingHours,
    retryPolicy,
    enabledTools: state.enabledTools,
    phoneNumberIds: [],
  };
}

function stateFromAgent(agent: VoiceAgent): WizardState {
  const script = agent.scriptTemplate;
  const dp = agent.disclosurePolicy;
  const ch = agent.callingHours;
  const rp = agent.retryPolicy;

  const callingDays = buildDefaultCallingDays();
  if (ch?.windows?.length) {
    for (const d of WEEK_DAYS) {
      callingDays[d] = { enabled: false, start: '09:00', end: '18:00' };
    }
    for (const w of ch.windows) {
      if (callingDays[w.day as WeekDay] !== undefined) {
        callingDays[w.day as WeekDay] = { enabled: true, start: w.start, end: w.end };
      }
    }
  }

  return {
    name: agent.name,
    description: agent.description ?? '',
    useCase: agent.useCase,
    language: agent.language,
    tone: agent.tone,
    voiceId: agent.voiceId,
    scriptOpening: script?.opening ?? '',
    scriptCta: script?.cta ?? '',
    scriptClosing: script?.closing ?? '',
    autoDetectLanguage: agent.autoDetectLanguage ?? false,
    alwaysDisclose: dp?.alwaysDisclose ?? true,
    recordingAnnouncement: dp?.recordingAnnouncement ?? true,
    disclosureWording: dp?.wording ?? '',
    useLeadTimezone: ch?.useLeadTimezone ?? true,
    callingDays,
    blackoutDates: ch?.blackoutDates ?? [],
    retryMaxAttempts: String(rp?.maxAttempts ?? 3),
    kbSeedText: '',
    enabledTools: agent.enabledTools ?? [],
  };
}

function initialState(): WizardState {
  return {
    name: '',
    description: '',
    useCase: 'custom',
    language: 'es_CO',
    tone: 'friendly_formal',
    voiceId: '',
    scriptOpening: '',
    scriptCta: '',
    scriptClosing: '',
    autoDetectLanguage: false,
    alwaysDisclose: true,
    recordingAnnouncement: true,
    disclosureWording: '',
    useLeadTimezone: true,
    callingDays: buildDefaultCallingDays(),
    blackoutDates: [],
    retryMaxAttempts: '3',
    kbSeedText: '',
    enabledTools: [],
  };
}

interface AgentWizardProps {
  organizationId: string;
  agent?: VoiceAgent;
  onComplete?: (agent: VoiceAgent) => void;
  onCancel?: () => void;
  // H12 — when 'onboarding', use the 5-step subset and emit onboarding
  // analytics events. Defaults to 'full' (legacy / explicit "New agent" CTA).
  mode?: 'full' | 'onboarding';
}

export function AgentWizard({
  organizationId,
  agent,
  onComplete,
  onCancel,
  mode = 'full',
}: AgentWizardProps) {
  const { t } = useTranslation();
  const { showError } = useToast();
  const { width } = useWindowDimensions();
  const isWideLayout = width >= 768;
  const STEPS: readonly WizardStep[] = mode === 'onboarding' ? ONBOARDING_STEPS : FULL_STEPS;
  const USE_CASE_OPTIONS = useCaseOptions(t);
  const LANGUAGE_OPTIONS = languageOptions(t);
  const TONE_OPTIONS = toneOptions(t);
  // FLOSEAI-655 — a draft resumes where the admin left off, and the rail shows
  // how far they actually got. A PUBLISHED agent is complete by definition, so
  // it keeps the previous behaviour: open at the start, every step ticked.
  const resumeIndex = resolveResumeStepIndex(agent, STEPS);
  const [stepIndex, setStepIndex] = useState(resumeIndex);
  const [furthestReachedIndex, setFurthestReachedIndex] = useState(
    agent && agent.status !== 'draft' ? STEPS.length - 1 : resumeIndex,
  );
  const [completedStepIndexes, setCompletedStepIndexes] = useState<Set<number>>(() => {
    // FLOSEAI-655 (QA): a published agent opens on step 1, so ticking its steps
    // told the admin they had passed nine steps they had not touched — the rail
    // said "9 done" beside a header reading "PASO 1 DE 10". Read as a broken
    // draft, and fairly so. Every step stays reachable through
    // furthestReachedIndex below; none is claimed as walked.
    if (agent && agent.status !== 'draft') {
      return new Set<number>();
    }
    // Everything before where they stopped was passed through; nothing after it
    // was, and ticking those was what made a half-built draft look finished.
    return new Set(Array.from({ length: resumeIndex }, (_, index) => index));
  });
  const [state, setState] = useState<WizardState>(() =>
    agent ? stateFromAgent(agent) : initialState(),
  );
  const [validationError, setValidationError] = useState<string | null>(null);
  // What the last autosave actually did. The review step used to claim a
  // saved draft purely because an agent existed (FLOSEAI-656).
  const [saveError, setSaveError] = useState<string | null>(null);
  const [blackoutDateInput, setBlackoutDateInput] = useState('');
  const [blackoutDateError, setBlackoutDateError] = useState<string | null>(null);
  const [selectedTemplateUseCase, setSelectedTemplateUseCase] = useState<VoiceAgentUseCase | null>(
    null,
  );
  // Follows the agent for the same reason every other field is hydrated from it:
  // reopening one that already had knowledge showed an empty list (FLOSEAI-659).
  const [knowledgeItems, setKnowledgeItems] = useState<KnowledgeBaseItem[]>(
    () => agent?.knowledgeItems ?? [],
  );
  const seedSubmittedRef = useRef(false);

  // Both follow the selected language: the curated template copy and the
  // fallback text a variable advertises are written per language (FLOSEAI-663).
  const templatesQuery = useVoiceAgentTemplates(state.language);
  const scriptVariablesQuery = useVoiceAgentScriptVariables(state.useCase, state.language);
  const voicesQuery = useVoiceLibrary(state.language);
  const createAgent = useCreateVoiceAgent();
  const updateAgent = useUpdateVoiceAgent();
  // FLOSEAI-656 — the one field a published agent may still change.
  const renameAgent = useRenameVoiceAgent();
  const publishAgent = usePublishVoiceAgent();
  const uploadKnowledgeItem = useUploadKnowledgeItem();
  const mp = useMixpanel();

  const voices = voicesQuery.data ?? [];
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedAgentRef = useRef<VoiceAgent | null>(agent ?? null);
  const stepEnteredAtRef = useRef<number>(Date.now());
  const wizardStartedAtRef = useRef<number>(Date.now());
  const templateAppliedRef = useRef(false);

  const update = useCallback(
    (patch: Partial<WizardState>) => {
      setState((prev) => {
        const next = { ...prev, ...patch };

        if (debounceRef.current) clearTimeout(debounceRef.current);

        debounceRef.current = setTimeout(() => {
          const saved = savedAgentRef.current;
          if (!saved) return;

          // FLOSEAI-656 — publishing freezes a configuration, and the generic
          // update rejects a published agent outright ("Only draft agents can be
          // updated"). The NAME is the exception the product carved out, so it
          // travels on its own endpoint. Everything else genuinely cannot be
          // saved while published, and saying so is the point: the rejection
          // used to be swallowed, leaving the wizard showing the typed value and
          // "Borrador guardado" over a write that never happened.
          if (saved.status !== 'draft') {
            const trimmed = next.name.trim();
            const nameChanged = trimmed.length > 0 && trimmed !== saved.name;

            if (nameChanged) {
              renameAgent.mutate(
                { id: saved.id, organizationId, name: trimmed },
                {
                  onSuccess: (updated) => {
                    savedAgentRef.current = updated;
                    setSaveError(null);
                  },
                  onError: (error) => setSaveError(error.message),
                },
              );
              return;
            }

            setSaveError(t('voiceAgents.wizard.publishedOnlyNameEditable'));
            showError(t('voiceAgents.wizard.publishedOnlyNameEditable'));
            return;
          }

          const dto: UpdateVoiceAgentDto = buildDtoFromState(next);

          updateAgent.mutate(
            { id: saved.id, organizationId, dto },
            {
              onSuccess: (updated) => {
                savedAgentRef.current = updated;
                setSaveError(null);
              },
              // Silently dropping this is what told the user a change had been
              // applied when it had not.
              onError: (error) => {
                setSaveError(error.message);
                showError(error.message);
              },
            },
          );
        }, 1000);

        return next;
      });
    },
    [organizationId, updateAgent, renameAgent, showError, t],
  );

  /**
   * Re-sync when the agent's knowledge arrives or changes (FLOSEAI-659, QA).
   *
   * Seeding once at mount was not enough. Uploading from the agent detail page
   * invalidates the detail query, but that refetch is asynchronous: opening
   * "Editar" straight afterwards mounts this wizard against the still-cached
   * agent, seeds an empty list, and a useState initializer never runs again. The
   * user then saw "no knowledge added" on the one screen they need in order to
   * publish, and had to upload the file a second time.
   *
   * The server list is the truth here. Items added inside the wizard are
   * appended locally AND invalidate the detail query, so they come back in the
   * next payload rather than being lost by this sync.
   */
  useEffect(() => {
    setKnowledgeItems(agent?.knowledgeItems ?? []);
  }, [agent?.knowledgeItems]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const currentStep: WizardStep = STEPS[stepIndex];
  const isLastStep = stepIndex === STEPS.length - 1;
  const isBusy = createAgent.isPending || updateAgent.isPending || publishAgent.isPending;
  const isCurrentStepValid = (() => {
    if (currentStep === 'Use Case') return selectedTemplateUseCase !== null;
    if (currentStep === 'Identity') return Boolean(state.name.trim());
    if (currentStep === 'Voice') return Boolean(state.voiceId.trim());
    if (currentStep === 'Script') return Boolean(state.scriptOpening.trim());
    if (currentStep === 'Calling Hours') {
      return WEEK_DAYS.every((day) => {
        const window = state.callingDays[day];
        return !window.enabled || window.start < window.end;
      });
    }
    return true;
  })();
  const isContinueDisabled = isBusy || !isCurrentStepValid;

  const applyTemplate = useCallback((template: UseCaseTemplate) => {
    setSelectedTemplateUseCase(template.useCase);
    templateAppliedRef.current = true;
    setState((prev) => {
      const callingDays = buildDefaultCallingDays();
      for (const d of WEEK_DAYS) {
        callingDays[d] = { enabled: false, start: '09:00', end: '18:00' };
      }
      for (const w of template.callingHours.windows) {
        callingDays[w.day as WeekDay] = { enabled: true, start: w.start, end: w.end };
      }
      return {
        ...prev,
        name: prev.name || template.defaultName,
        description: prev.description || template.defaultDescription,
        useCase: template.useCase,
        language: template.defaultLanguage,
        tone: template.defaultTone,
        scriptOpening: template.scriptTemplate.opening,
        scriptCta: template.scriptTemplate.cta,
        scriptClosing: template.scriptTemplate.closing,
        useLeadTimezone: template.callingHours.useLeadTimezone,
        callingDays,
        enabledTools: template.enabledTools,
      };
    });
  }, []);

  const startBlank = useCallback(() => {
    setSelectedTemplateUseCase('custom');

    // Only reset wizard state when a template had actually been applied
    // beforehand — otherwise this would wipe out data restored from an
    // existing agent (edit flow) or fields the admin already filled in.
    // When a template WAS applied, "Start blank" must clear everything it
    // pre-filled (name, description, scripts, tools, useCase) — otherwise the
    // template's curated content leaks into the blank agent.
    if (templateAppliedRef.current) {
      templateAppliedRef.current = false;
      setState(initialState());
    }
  }, []);

  async function handleNext() {
    setValidationError(null);

    if (currentStep === 'Use Case' && selectedTemplateUseCase === null) {
      setValidationError(t('voiceAgents.wizard.errors.useCaseRequired'));
      return;
    }
    if (currentStep === 'Identity' && !state.name.trim()) {
      setValidationError(t('voiceAgents.wizard.errors.nameRequired'));
      return;
    }
    if (currentStep === 'Voice' && !state.voiceId.trim()) {
      setValidationError(t('voiceAgents.wizard.errors.voiceIdRequired'));
      return;
    }
    if (currentStep === 'Script' && !state.scriptOpening.trim()) {
      setValidationError(t('voiceAgents.wizard.errors.openingRequired'));
      return;
    }
    if (
      currentStep === 'Calling Hours' &&
      WEEK_DAYS.some((day) => {
        const window = state.callingDays[day];
        return window.enabled && window.start >= window.end;
      })
    ) {
      setValidationError(t('voiceAgents.wizard.errors.callingHoursRangeInvalid'));
      return;
    }

    // Create the draft once the admin finishes the Identity step (the first
    // step that gathers the minimum fields required by the CreateVoiceAgentDto).
    if (!savedAgentRef.current && currentStep === 'Identity') {
      const dto = buildDtoFromState(state);

      if (!dto.scriptTemplate.opening) {
        dto.scriptTemplate = { opening: 'Draft opening.', cta: 'Book', closing: 'Goodbye.' };
      }

      try {
        const created = await createAgent.mutateAsync({ organizationId, dto });
        savedAgentRef.current = created;
      } catch {
        setValidationError(t('voiceAgents.wizard.errors.draftSaveFailed'));
        return;
      }
    }

    // Onboarding-only: persist the optional "quick context" seed as a text KB
    // item when the admin leaves the Knowledge step. Fire-and-forget once so a
    // slow/failed upload never blocks the fast onboarding path.
    if (
      mode === 'onboarding' &&
      currentStep === 'Knowledge' &&
      savedAgentRef.current &&
      state.kbSeedText.trim() &&
      !seedSubmittedRef.current
    ) {
      seedSubmittedRef.current = true;
      const seed = state.kbSeedText.trim();
      const agentId = savedAgentRef.current.id;
      void (async () => {
        try {
          const checksum = await Crypto.digestStringAsync(
            Crypto.CryptoDigestAlgorithm.SHA256,
            seed,
          );
          const item = await uploadKnowledgeItem.mutateAsync({
            agentId,
            organizationId,
            dto: {
              title: t('voiceAgents.wizard.knowledge.seedTitle'),
              sourceType: 'text',
              contentText: seed,
              checksum,
            },
          });
          setKnowledgeItems((prev) => [...prev, item]);
        } catch {
          seedSubmittedRef.current = false;
        }
      })();
    }

    if (stepIndex < STEPS.length - 1) {
      if (mode === 'onboarding') {
        trackEvent(mp, 'voice_agent.onboarding_step_completed', {
          step: currentStep,
          step_index: stepIndex,
          duration_ms: Date.now() - stepEnteredAtRef.current,
        });
        stepEnteredAtRef.current = Date.now();
      }
      setCompletedStepIndexes((completed) => new Set(completed).add(stepIndex));
      setFurthestReachedIndex((furthest) => Math.max(furthest, stepIndex + 1));
      goToStep(stepIndex + 1);
    }
  }

  async function handlePublish() {
    setValidationError(null);

    if (!savedAgentRef.current) {
      const msg = t('voiceAgents.wizard.errors.saveBeforePublish');
      setValidationError(msg);
      showError(msg);
      return;
    }

    if (!state.alwaysDisclose) {
      const msg = t('voiceAgents.wizard.errors.discloseRequired');
      setValidationError(msg);
      showError(msg);
      return;
    }

    if (!state.recordingAnnouncement) {
      const msg = t('voiceAgents.wizard.errors.recordingAnnouncementRequired');
      setValidationError(msg);
      showError(msg);
      return;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    try {
      const published = await publishAgent.mutateAsync({
        id: savedAgentRef.current.id,
        organizationId,
      });
      if (mode === 'onboarding') {
        trackEvent(mp, 'voice_agent.onboarding_completed', {
          agent_id: published.id,
          total_duration_ms: Date.now() - wizardStartedAtRef.current,
        });
      }
      onComplete?.(published);
    } catch (e) {
      const backendMessage = (e as { response?: { data?: { message?: string } } })?.response?.data
        ?.message;
      const msg = backendMessage || t('voiceAgents.wizard.errors.publishFailed');
      setValidationError(msg);
      showError(msg);
    }
  }

  function handleCancel() {
    if (mode === 'onboarding') {
      trackEvent(mp, 'voice_agent.onboarding_skipped', { at_step: currentStep });
    }
    onCancel?.();
  }

  /**
   * The single place the visible step changes (FLOSEAI-655).
   *
   * Field edits are autosaved by `update`, but moving between steps changes no
   * field, so the position would never be written and reopening the draft would
   * restart at the beginning. Sent immediately rather than debounced: leaving
   * the wizard is exactly when the admin stops typing, so a pending debounce
   * would be the thing that never lands.
   */
  function goToStep(next: number) {
    setStepIndex(next);

    const saved = savedAgentRef.current;
    if (!saved || saved.status !== 'draft') return;

    updateAgent.mutate({ id: saved.id, organizationId, dto: { draftStep: STEPS[next] } });
  }

  function navigateToReachedStep(index: number) {
    if (index > furthestReachedIndex || isBusy) return;
    setValidationError(null);
    goToStep(index);
  }

  function renderWizardActions(testID: string) {
    return (
      <View
        testID={testID}
        accessibilityLabel={Platform.OS === 'web' ? testID : undefined}
        style={styles.commandActions}
      >
        {!isLastStep ? (
          <VStack spacing="xs" style={styles.nextStepContext}>
            <Caption style={styles.nextStepEyebrow}>
              {t('voiceAgents.wizard.footer.continueToPrefix')}
            </Caption>
            <Body style={styles.nextStepName}>{stepLabel(t, STEPS[stepIndex + 1])}</Body>
          </VStack>
        ) : null}
        <HStack spacing="sm" style={styles.commandButtons}>
          {stepIndex > 0 ? (
            <Button
              variant="outline"
              size="sm"
              onPress={() => goToStep(stepIndex - 1)}
              accessibilityLabel={t('voiceAgents.wizard.footer.backA11y')}
              disabled={isBusy}
              style={styles.commandButtonSecondary}
            >
              {t('voiceAgents.wizard.footer.back')}
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onPress={handleCancel}
              accessibilityLabel={t('voiceAgents.wizard.footer.cancelA11y')}
              disabled={isBusy}
              style={styles.commandButtonSecondary}
            >
              {t('voiceAgents.wizard.footer.cancel')}
            </Button>
          )}

          {isLastStep ? (
            <Button
              variant="primary"
              size="sm"
              onPress={handlePublish}
              disabled={isBusy}
              accessibilityLabel={t('voiceAgents.wizard.footer.publishA11y')}
              style={styles.commandButtonPrimary}
            >
              {publishAgent.isPending ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                t('voiceAgents.wizard.footer.publish')
              )}
            </Button>
          ) : (
            <Button
              variant="primary"
              size="sm"
              onPress={handleNext}
              disabled={isContinueDisabled}
              accessibilityLabel={`${t('voiceAgents.wizard.footer.continueToPrefix')} ${stepLabel(t, STEPS[stepIndex + 1])}`}
              style={[
                styles.commandButtonPrimary,
                isContinueDisabled && styles.commandButtonPrimaryDisabled,
              ]}
              textColor={isContinueDisabled ? colors.text.disabled : undefined}
            >
              {isBusy && currentStep === 'Identity' ? (
                <ActivityIndicator size="small" color={colors.text.disabled} />
              ) : (
                t('voiceAgents.wizard.footer.continue')
              )}
            </Button>
          )}
        </HStack>
      </View>
    );
  }

  function renderStepIndicator() {
    return (
      <View style={styles.stepHeader}>
        <View style={[styles.stepHeaderRail, isWideLayout && styles.stepHeaderRailWide]}>
          <VStack
            spacing="xs"
            style={[styles.stepTitleBlock, isWideLayout && styles.stepTitleBlockWide]}
          >
            <Caption style={styles.stepCounter}>
              {`${t('voiceAgents.wizard.stepCounter')} ${stepIndex + 1} ${t('voiceAgents.wizard.stepCounterOf')} ${STEPS.length}`}
            </Caption>
            <Heading3 style={styles.stepTitle} accessibilityRole="header" accessible>
              {stepLabel(t, currentStep)}
            </Heading3>
          </VStack>

          <View style={[styles.stepCommandPanel, isWideLayout && styles.stepCommandPanelWide]}>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              accessibilityRole="tablist"
              accessibilityLabel={t('voiceAgents.wizard.stepsA11yLabel')}
              contentContainerStyle={[styles.stepList, isWideLayout && styles.stepListWide]}
            >
              {STEPS.map((step, i) => {
                const isCurrent = i === stepIndex;
                const isComplete = completedStepIndexes.has(i);
                const isUnlocked = i <= furthestReachedIndex;
                const stepHint = isComplete
                  ? t('voiceAgents.wizard.steps.completedHint')
                  : isUnlocked
                    ? t('voiceAgents.wizard.steps.availableHint')
                    : t('voiceAgents.wizard.steps.lockedHint');
                return (
                  <React.Fragment key={step}>
                    {i > 0 ? (
                      <View
                        style={[
                          styles.stepConnector,
                          completedStepIndexes.has(i - 1) && styles.stepConnectorDone,
                        ]}
                        accessibilityElementsHidden
                        importantForAccessibility="no-hide-descendants"
                      />
                    ) : null}
                    <Pressable
                      onPress={() => navigateToReachedStep(i)}
                      disabled={!isUnlocked || isBusy}
                      accessibilityRole="tab"
                      accessibilityLabel={
                        Platform.OS === 'web'
                          ? `${stepLabel(t, step)}. ${stepHint}`
                          : stepLabel(t, step)
                      }
                      accessibilityHint={stepHint}
                      accessibilityState={{ selected: isCurrent, disabled: !isUnlocked || isBusy }}
                      aria-selected={isCurrent}
                      style={styles.stepButton}
                    >
                      <View
                        style={[
                          styles.stepNumber,
                          isCurrent && styles.stepNumberActive,
                          isComplete && styles.stepNumberDone,
                        ]}
                      >
                        {isComplete ? (
                          <Check size={14} strokeWidth={2.5} color={colors.text.inverse} />
                        ) : (
                          <Caption
                            style={[
                              styles.stepNumberText,
                              isCurrent && styles.stepNumberTextActive,
                            ]}
                          >
                            {String(i + 1).padStart(2, '0')}
                          </Caption>
                        )}
                      </View>
                    </Pressable>
                  </React.Fragment>
                );
              })}
            </ScrollView>
            {isWideLayout ? renderWizardActions('wizard-header-actions') : null}
          </View>
        </View>
      </View>
    );
  }

  function _renderStepTitle() {
    return null;
  }

  function renderStepContent() {
    switch (currentStep) {
      case 'Use Case':
        return (
          <VStack spacing="xl">
            <VStack spacing="xs" style={styles.useCaseIntro}>
              <Caption style={styles.sectionEyebrow}>
                {t('voiceAgents.wizard.useCaseStep.eyebrow')}
              </Caption>
              <Body style={styles.sectionNote}>{t('voiceAgents.wizard.useCaseStep.intro')}</Body>
            </VStack>

            {templatesQuery.isPending ? (
              <ActivityIndicator
                accessibilityLabel={t('voiceAgents.wizard.useCaseStep.loadingTemplates')}
                color={colors.primary.main}
              />
            ) : null}

            {templatesQuery.isError ? (
              <Body style={styles.error} accessibilityLiveRegion="polite">
                {t('voiceAgents.wizard.useCaseStep.loadError')}
              </Body>
            ) : null}

            <View
              testID="use-case-grid"
              accessibilityRole="radiogroup"
              accessibilityLabel={t('voiceAgents.wizard.useCaseStep.choicesA11y')}
              style={[
                styles.templateGrid,
                isWideLayout ? styles.templateGridWide : styles.templateGridCompact,
              ]}
            >
              {templatesQuery.data?.map((tpl) => {
                const selected = selectedTemplateUseCase === tpl.useCase;
                const nameKey = `voiceAgents.wizard.templates.${tpl.useCase}.name`;
                const descKey = `voiceAgents.wizard.templates.${tpl.useCase}.description`;
                const translatedName = t(nameKey);
                const translatedDesc = t(descKey);
                const name = translatedName === nameKey ? tpl.defaultName : translatedName;
                const description =
                  translatedDesc === descKey ? tpl.defaultDescription : translatedDesc;
                const UseCaseIcon = USE_CASE_ICONS[tpl.useCase];
                return (
                  <Pressable
                    key={tpl.useCase}
                    onPress={() => applyTemplate(tpl)}
                    accessibilityRole="radio"
                    accessibilityLabel={`${t('voiceAgents.wizard.useCaseStep.useTemplatePrefix')} ${name} ${t('voiceAgents.wizard.useCaseStep.useTemplateSuffix')}`}
                    accessibilityState={{ checked: selected }}
                    aria-checked={selected}
                    style={({ pressed }) => [
                      styles.templateCard,
                      isWideLayout && styles.templateCardWide,
                      selected && styles.templateCardSelected,
                      pressed && styles.templateCardPressed,
                    ]}
                  >
                    <HStack style={styles.templateCardTopRow}>
                      <View style={[styles.templateIcon, selected && styles.templateIconSelected]}>
                        <UseCaseIcon
                          size={20}
                          strokeWidth={1.8}
                          color={selected ? colors.primary.neon : colors.text.secondary}
                        />
                      </View>
                      <View
                        style={[styles.templateRadio, selected && styles.templateRadioSelected]}
                      >
                        {selected ? <View style={styles.templateRadioDot} /> : null}
                      </View>
                    </HStack>
                    <VStack spacing="xs" style={styles.templateCardCopy}>
                      <Caption style={styles.templateCardUseCase}>
                        {t(`voiceAgents.wizard.useCase.${tpl.useCase}`)}
                      </Caption>
                      <Heading4 style={styles.templateCardTitle}>{name}</Heading4>
                      <Body style={styles.templateCardDescription}>{description}</Body>
                    </VStack>
                  </Pressable>
                );
              })}

              <Pressable
                onPress={startBlank}
                accessibilityRole="radio"
                accessibilityLabel={t('voiceAgents.wizard.useCaseStep.startBlankA11y')}
                accessibilityState={{ checked: selectedTemplateUseCase === 'custom' }}
                aria-checked={selectedTemplateUseCase === 'custom'}
                style={({ pressed }) => [
                  styles.templateCard,
                  styles.templateCardBlank,
                  isWideLayout && styles.templateCardWide,
                  selectedTemplateUseCase === 'custom' && styles.templateCardSelected,
                  pressed && styles.templateCardPressed,
                ]}
              >
                <HStack style={styles.templateCardTopRow}>
                  <View
                    style={[
                      styles.templateIcon,
                      selectedTemplateUseCase === 'custom' && styles.templateIconSelected,
                    ]}
                  >
                    <Sparkles
                      size={20}
                      strokeWidth={1.8}
                      color={
                        selectedTemplateUseCase === 'custom'
                          ? colors.primary.neon
                          : colors.text.secondary
                      }
                    />
                  </View>
                  <View
                    style={[
                      styles.templateRadio,
                      selectedTemplateUseCase === 'custom' && styles.templateRadioSelected,
                    ]}
                  >
                    {selectedTemplateUseCase === 'custom' ? (
                      <View style={styles.templateRadioDot} />
                    ) : null}
                  </View>
                </HStack>
                <VStack spacing="xs" style={styles.templateCardCopy}>
                  <Caption style={styles.templateCardUseCase}>
                    {t('voiceAgents.wizard.useCase.custom')}
                  </Caption>
                  <Heading4 style={styles.templateCardTitle}>
                    {t('voiceAgents.wizard.useCaseStep.startBlankTitle')}
                  </Heading4>
                  <Body style={styles.templateCardDescription}>
                    {t('voiceAgents.wizard.useCaseStep.startBlankDescription')}
                  </Body>
                </VStack>
              </Pressable>
            </View>
          </VStack>
        );

      case 'Identity':
        return (
          <VStack spacing="lg">
            <LabeledInput
              label={t('voiceAgents.wizard.identity.nameLabel')}
              value={state.name}
              onChangeText={(v) => update({ name: v })}
              placeholder={t('voiceAgents.wizard.identity.namePlaceholder')}
              accessibilityLabel={t('voiceAgents.wizard.identity.nameA11y')}
            />
            <LabeledInput
              label={t('voiceAgents.wizard.identity.descriptionLabel')}
              value={state.description}
              onChangeText={(v) => update({ description: v })}
              placeholder={t('voiceAgents.wizard.identity.descriptionPlaceholder')}
              multiline
              accessibilityLabel={t('voiceAgents.wizard.identity.descriptionA11y')}
            />
            <SelectField
              label={t('voiceAgents.wizard.identity.useCaseLabel')}
              value={state.useCase}
              options={USE_CASE_OPTIONS}
              onSelect={(v) => update({ useCase: v as VoiceAgentUseCase })}
              accessibilityLabel={t('voiceAgents.wizard.identity.useCaseA11y')}
            />
            <SelectField
              label={t('voiceAgents.wizard.identity.languageLabel')}
              value={state.language}
              options={LANGUAGE_OPTIONS}
              onSelect={(v) => update({ language: v as VoiceAgentLanguage })}
              accessibilityLabel={t('voiceAgents.wizard.identity.languageA11y')}
            />
            <ToggleRow
              label={t('voiceAgents.wizard.identity.autoDetectLanguageLabel')}
              value={state.autoDetectLanguage}
              onValueChange={(v) => update({ autoDetectLanguage: v })}
              accessibilityLabel={t('voiceAgents.wizard.identity.autoDetectLanguageA11y')}
            />
            <Caption style={{ color: colors.text.secondary }}>
              {t('voiceAgents.wizard.identity.autoDetectLanguageHint')}
            </Caption>
            <SelectField
              label={t('voiceAgents.wizard.identity.toneLabel')}
              value={state.tone}
              options={TONE_OPTIONS}
              onSelect={(v) => update({ tone: v })}
              accessibilityLabel={t('voiceAgents.wizard.identity.toneA11y')}
            />
          </VStack>
        );

      case 'Voice':
        return (
          <VStack spacing="lg">
            <VStack spacing="xs">
              <Label style={styles.inputLabel}>{t('voiceAgents.wizard.voice.selectLabel')}</Label>
              <Caption style={styles.hint}>{t('voiceAgents.wizard.voice.catalogHint')}</Caption>
            </VStack>

            {voicesQuery.isLoading ? (
              <HStack spacing="sm" style={{ alignItems: 'center' }}>
                <ActivityIndicator size="small" color={colors.primary[500]} />
                <Caption style={styles.hint}>{t('voiceAgents.wizard.voice.loading')}</Caption>
              </HStack>
            ) : null}

            {!voicesQuery.isLoading && voices.length > 0 ? (
              <VStack spacing="sm">
                {voices.map((voice) => {
                  const selected = state.voiceId === voice.voiceId;
                  return (
                    <View
                      key={voice.voiceId}
                      style={[styles.voiceCard, selected && styles.voiceCardSelected]}
                    >
                      <Pressable
                        style={styles.voiceSelectArea}
                        onPress={() => update({ voiceId: voice.voiceId })}
                        accessibilityRole="radio"
                        accessibilityState={{ selected }}
                        accessibilityLabel={`${t('voiceAgents.wizard.voice.selectVoiceA11yPrefix')} ${voice.name}`}
                        testID={`voice-option-${voice.voiceId}`}
                      >
                        <View style={[styles.voiceRadio, selected && styles.voiceRadioSelected]}>
                          {selected ? <View style={styles.voiceRadioDot} /> : null}
                        </View>
                        <VStack spacing="xxs" style={styles.voiceInfo}>
                          <Body style={styles.voiceName}>{voice.name}</Body>
                          {voice.category ? (
                            <Caption style={styles.voiceCategory}>{voice.category}</Caption>
                          ) : null}
                        </VStack>
                      </Pressable>
                      <VoicePreviewButton
                        previewUrl={voice.previewUrl}
                        voiceId={voice.voiceId}
                        activeVoiceId={playingVoiceId}
                        onPlayStart={setPlayingVoiceId}
                        testID={`voice-preview-${voice.voiceId}`}
                      />
                    </View>
                  );
                })}
              </VStack>
            ) : null}

            {voicesQuery.isError ? (
              <Caption style={styles.hint}>{t('voiceAgents.wizard.voice.loadError')}</Caption>
            ) : null}
            {!voicesQuery.isLoading && !voicesQuery.isError && voices.length === 0 ? (
              <Caption style={styles.hint}>{t('voiceAgents.wizard.voice.empty')}</Caption>
            ) : null}
          </VStack>
        );

      case 'Script':
        return (
          <ScriptVariableBuilder
            opening={state.scriptOpening}
            cta={state.scriptCta}
            closing={state.scriptClosing}
            variables={scriptVariablesQuery.data ?? []}
            isLoading={scriptVariablesQuery.isPending}
            isError={scriptVariablesQuery.isError}
            onOpeningChange={(scriptOpening) => update({ scriptOpening })}
            onCtaChange={(scriptCta) => update({ scriptCta })}
            onClosingChange={(scriptClosing) => update({ scriptClosing })}
          />
        );

      case 'Knowledge':
        return (
          <VStack spacing="lg">
            <Body style={styles.sectionNote}>
              {mode === 'onboarding'
                ? t('voiceAgents.wizard.knowledge.introOnboarding')
                : t('voiceAgents.wizard.knowledge.introFull')}
            </Body>
            {mode === 'onboarding' ? (
              <>
                <LabeledInput
                  label={t('voiceAgents.wizard.knowledge.seedLabel')}
                  value={state.kbSeedText}
                  onChangeText={(v) => update({ kbSeedText: v })}
                  placeholder={t('voiceAgents.wizard.knowledge.seedPlaceholder')}
                  multiline
                  accessibilityLabel={t('voiceAgents.wizard.knowledge.seedA11y')}
                />
                <Caption style={styles.sectionNote}>
                  {t('voiceAgents.wizard.knowledge.skipHint')}
                </Caption>
              </>
            ) : null}
            <KnowledgeManager
              agentId={savedAgentRef.current?.id ?? null}
              organizationId={organizationId}
              items={knowledgeItems}
              onItemAdded={(item) => setKnowledgeItems((prev) => [...prev, item])}
              onItemRemoved={(itemId) =>
                setKnowledgeItems((prev) => prev.filter((i) => i.id !== itemId))
              }
            />
          </VStack>
        );

      case 'Objections':
        return (
          <VStack spacing="md">
            <Body style={styles.sectionNote}>{t('voiceAgents.wizard.objections.note')}</Body>
          </VStack>
        );

      case 'Tools & Compliance':
        return (
          <VStack spacing="lg">
            <Heading4 style={styles.subHeading}>
              {t('voiceAgents.wizard.compliance.heading')}
            </Heading4>
            <ToggleRow
              label={t('voiceAgents.wizard.compliance.alwaysDiscloseLabel')}
              value={state.alwaysDisclose}
              onValueChange={(v) => update({ alwaysDisclose: v })}
              accessibilityLabel={t('voiceAgents.wizard.compliance.alwaysDiscloseA11y')}
            />
            <ToggleRow
              label={t('voiceAgents.wizard.compliance.recordingLabel')}
              value={state.recordingAnnouncement}
              onValueChange={(v) => update({ recordingAnnouncement: v })}
              accessibilityLabel={t('voiceAgents.wizard.compliance.recordingA11y')}
            />
            <LabeledInput
              label={t('voiceAgents.wizard.compliance.wordingLabel')}
              value={state.disclosureWording}
              onChangeText={(v) => update({ disclosureWording: v })}
              placeholder={t('voiceAgents.wizard.compliance.wordingPlaceholder')}
              multiline
              accessibilityLabel={t('voiceAgents.wizard.compliance.wordingA11y')}
            />

            <Heading4 style={styles.subHeading}>{t('voiceAgents.wizard.tools.heading')}</Heading4>
            <Caption style={styles.hint}>{t('voiceAgents.wizard.tools.hint')}</Caption>
            <VStack spacing="xs" accessibilityRole="list" accessibilityLabel="enabled-tools-list">
              {ALL_TOOL_VALUES.map((tool) => {
                const checked = state.enabledTools.includes(tool);
                return (
                  <ToggleRow
                    key={tool}
                    label={t(`voiceAgents.wizard.tools.${tool}`)}
                    value={checked}
                    onValueChange={(v) => {
                      update({
                        enabledTools: v
                          ? [...state.enabledTools, tool]
                          : state.enabledTools.filter((t2) => t2 !== tool),
                      });
                    }}
                    accessibilityLabel={`tool-toggle-${tool}`}
                  />
                );
              })}
            </VStack>
          </VStack>
        );

      case 'Calling Hours': {
        const DAY_FULL_LABELS: Record<WeekDay, string> = {
          mon: t('voiceAgents.wizard.callingHours.dayMon'),
          tue: t('voiceAgents.wizard.callingHours.dayTue'),
          wed: t('voiceAgents.wizard.callingHours.dayWed'),
          thu: t('voiceAgents.wizard.callingHours.dayThu'),
          fri: t('voiceAgents.wizard.callingHours.dayFri'),
          sat: t('voiceAgents.wizard.callingHours.daySat'),
          sun: t('voiceAgents.wizard.callingHours.daySun'),
        };
        return (
          <VStack spacing="lg">
            <ToggleRow
              label={t('voiceAgents.wizard.callingHours.useLeadTimezoneLabel')}
              value={state.useLeadTimezone}
              onValueChange={(v) => update({ useLeadTimezone: v })}
              accessibilityLabel={t('voiceAgents.wizard.callingHours.useLeadTimezoneA11y')}
            />

            <VStack spacing="xs">
              <Label style={styles.inputLabel}>
                {t('voiceAgents.wizard.callingHours.callingDaysLabel')}
              </Label>
              {WEEK_DAYS.map((day) => {
                const dayData = state.callingDays[day];
                const dayName = DAY_FULL_LABELS[day];
                return (
                  <View key={day} style={styles.dayRow}>
                    <Body style={styles.dayLabel}>{DAY_SHORT_LABELS[day]}</Body>
                    <Switch
                      value={dayData.enabled}
                      onValueChange={(v) => {
                        update({
                          callingDays: {
                            ...state.callingDays,
                            [day]: { ...dayData, enabled: v },
                          },
                        });
                      }}
                      accessible
                      accessibilityRole="switch"
                      accessibilityLabel={`day-toggle-${day}`}
                      accessibilityState={{ checked: dayData.enabled }}
                      testID={`day-toggle-${day}`}
                      trackColor={{
                        false: colors.background.elevated,
                        true: colors.primary.main,
                      }}
                      thumbColor={colors.foreground}
                    />
                    {dayData.enabled ? (
                      <View style={styles.dayTimeWrap}>
                        <TimePickerInput
                          value={dayData.start}
                          onChange={(start) => {
                            update({
                              callingDays: {
                                ...state.callingDays,
                                [day]: { ...dayData, start },
                              },
                            });
                          }}
                          accessibilityLabel={t('voiceAgents.wizard.callingHours.dayStartA11y', {
                            day: dayName,
                          })}
                          testID={`calling-time-${day}-start`}
                        />
                        <Body style={{ color: colors.text.tertiary }}>–</Body>
                        <TimePickerInput
                          value={dayData.end}
                          onChange={(end) => {
                            update({
                              callingDays: {
                                ...state.callingDays,
                                [day]: { ...dayData, end },
                              },
                            });
                          }}
                          accessibilityLabel={t('voiceAgents.wizard.callingHours.dayEndA11y', {
                            day: dayName,
                          })}
                          testID={`calling-time-${day}-end`}
                        />
                      </View>
                    ) : null}
                  </View>
                );
              })}
            </VStack>

            <VStack spacing="xs">
              <Label style={styles.inputLabel}>
                {t('voiceAgents.wizard.callingHours.blackoutDatesLabel')}
              </Label>
              <View style={styles.blackoutRow}>
                <DatePickerInput
                  value={blackoutDateInput}
                  onChange={(date) => {
                    setBlackoutDateInput(date);
                    setBlackoutDateError(null);
                  }}
                  placeholder={t('voiceAgents.wizard.callingHours.blackoutDatePlaceholder')}
                  accessibilityLabel={t('voiceAgents.wizard.callingHours.blackoutDatePlaceholder')}
                  testID="blackout-date-input"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onPress={() => {
                    const v = blackoutDateInput.trim();
                    if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) {
                      setBlackoutDateError(
                        t('voiceAgents.wizard.callingHours.blackoutDateInvalid'),
                      );
                      return;
                    }
                    if (!state.blackoutDates.includes(v)) {
                      update({ blackoutDates: [...state.blackoutDates, v] });
                    }
                    setBlackoutDateInput('');
                    setBlackoutDateError(null);
                  }}
                  accessibilityLabel={t('voiceAgents.wizard.callingHours.blackoutDateAddA11y')}
                  testID="blackout-date-add"
                >
                  {t('voiceAgents.wizard.callingHours.blackoutDateAddLabel')}
                </Button>
              </View>
              {blackoutDateError ? (
                <Caption
                  accessibilityLabel="blackout-date-error"
                  testID="blackout-date-error"
                  style={styles.error}
                >
                  {blackoutDateError}
                </Caption>
              ) : null}
              {state.blackoutDates.map((date) => (
                <View
                  key={date}
                  accessibilityLabel={`blackout-chip-${date}`}
                  testID={`blackout-chip-${date}`}
                  style={styles.blackoutChip}
                >
                  <Body style={{ flex: 1, color: colors.text.primary, fontSize: 14 }}>{date}</Body>
                  <Pressable
                    onPress={() => {
                      update({
                        blackoutDates: state.blackoutDates.filter((d) => d !== date),
                      });
                    }}
                    accessibilityRole="button"
                    accessibilityLabel={`blackout-remove-${date}`}
                    testID={`blackout-remove-${date}`}
                  >
                    <Body style={{ color: colors.text.tertiary, paddingHorizontal: 4 }}>×</Body>
                  </Pressable>
                </View>
              ))}
            </VStack>
          </VStack>
        );
      }

      case 'Retry Policy':
        return (
          <VStack spacing="lg">
            <LabeledInput
              label={t('voiceAgents.wizard.retry.maxAttemptsLabel')}
              value={state.retryMaxAttempts}
              onChangeText={(v) => update({ retryMaxAttempts: v })}
              keyboardType="numeric"
              placeholder="3"
              accessibilityLabel={t('voiceAgents.wizard.retry.maxAttemptsA11y')}
            />
          </VStack>
        );

      case 'Review & Publish': {
        const useCaseLabel =
          USE_CASE_OPTIONS.find((o) => o.value === state.useCase)?.label ?? state.useCase;
        const languageLabel =
          LANGUAGE_OPTIONS.find((o) => o.value === state.language)?.label ?? state.language;
        const toneLabel = TONE_OPTIONS.find((o) => o.value === state.tone)?.label ?? state.tone;
        const goToStep = (name: WizardStep) => () => {
          const i = STEPS.indexOf(name);
          if (i >= 0) navigateToReachedStep(i);
        };
        return (
          <VStack spacing="lg">
            <ReviewRow
              label={t('voiceAgents.wizard.review.name')}
              value={state.name}
              onEdit={goToStep('Identity')}
            />
            <ReviewRow
              label={t('voiceAgents.wizard.review.useCase')}
              value={useCaseLabel}
              onEdit={goToStep('Identity')}
            />
            <ReviewRow
              label={t('voiceAgents.wizard.review.language')}
              value={languageLabel}
              onEdit={goToStep('Identity')}
            />
            <ReviewRow
              label={t('voiceAgents.wizard.review.tone')}
              value={toneLabel}
              onEdit={goToStep('Identity')}
            />
            <ReviewRow
              label={t('voiceAgents.wizard.review.voiceId')}
              value={state.voiceId}
              onEdit={goToStep('Voice')}
            />
            <ReviewRow
              label={t('voiceAgents.wizard.review.opening')}
              value={state.scriptOpening}
              onEdit={goToStep('Script')}
            />
            <ReviewRow
              label={t('voiceAgents.wizard.review.cta')}
              value={state.scriptCta}
              onEdit={goToStep('Script')}
            />
            <ReviewRow
              label={t('voiceAgents.wizard.review.closing')}
              value={state.scriptClosing}
              onEdit={goToStep('Script')}
            />
            <ReviewRow
              label={t('voiceAgents.wizard.review.disclosure')}
              value={
                state.alwaysDisclose
                  ? t('voiceAgents.wizard.review.enabled')
                  : t('voiceAgents.wizard.review.disabled')
              }
              onEdit={goToStep('Tools & Compliance')}
            />
            <ReviewRow
              label={t('voiceAgents.wizard.review.callingHours')}
              value={(() => {
                const enabledDays = WEEK_DAYS.filter((d) => state.callingDays[d].enabled);
                if (enabledDays.length === 0) return '—';
                return `${enabledDays.map((d) => DAY_SHORT_LABELS[d]).join(', ')} · ${enabledDays.length} day(s)`;
              })()}
              onEdit={goToStep('Calling Hours')}
            />
            <ReviewRow
              label={t('voiceAgents.wizard.review.maxRetries')}
              value={state.retryMaxAttempts}
              onEdit={goToStep('Retry Policy')}
            />

            {saveError ? (
              <Body style={styles.errorText} accessibilityLiveRegion="assertive" role="alert">
                {saveError}
              </Body>
            ) : savedAgentRef.current ? (
              <Body style={styles.savedNote} accessibilityLiveRegion="polite">
                {`${t('voiceAgents.wizard.review.draftSaved')} ${savedAgentRef.current.id.slice(0, 8)}…`}
              </Body>
            ) : null}
          </VStack>
        );
      }
    }
  }

  return (
    <View style={styles.root} accessibilityLabel={t('voiceAgents.wizard.rootA11y')}>
      {renderStepIndicator()}

      <ScrollView
        style={styles.scrollArea}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        <Animated.View
          key={currentStep}
          entering={STEP_ENTERING}
          exiting={STEP_EXITING}
          style={styles.stepContent}
        >
          {mode === 'onboarding' ? <OnboardingVideoBanner stepName={currentStep} /> : null}
          {renderStepContent()}
        </Animated.View>

        {validationError ? (
          <Body
            style={styles.error}
            accessibilityLiveRegion="assertive"
            accessibilityRole="alert"
            accessibilityLabel={
              currentStep === 'Calling Hours' ? 'calling-hours-time-error' : undefined
            }
          >
            {validationError}
          </Body>
        ) : null}
        {!isWideLayout ? renderWizardActions('wizard-mobile-actions') : null}
      </ScrollView>
    </View>
  );
}

function OnboardingVideoBanner({ stepName }: { stepName: WizardStep }) {
  const { t } = useTranslation();
  const url = getOnboardingVideoUrl(stepName);
  const stepDisplayName = stepLabel(t, stepName);

  // Web → embed the video player iframe so the admin can play inline.
  // Native → render a tappable card that opens the URL externally so we
  // don't pull in a heavy player just for the onboarding flow.
  if (url && Platform.OS === 'web') {
    return (
      <View
        style={styles.videoEmbedWrap}
        accessible
        accessibilityRole="image"
        accessibilityLabel={`${t('voiceAgents.wizard.video.forStepA11y')} ${stepDisplayName}`}
      >
        {React.createElement('iframe', {
          src: url,
          style: { border: 0, width: '100%', height: 220, borderRadius: 12 },
          allow: 'autoplay; encrypted-media; picture-in-picture',
          allowFullScreen: true,
          title: `${stepDisplayName} ${t('voiceAgents.wizard.video.tutorialSuffix')}`,
        })}
      </View>
    );
  }

  if (url) {
    return (
      <Pressable
        onPress={() => Linking.openURL(url).catch(() => undefined)}
        accessibilityRole="button"
        accessibilityLabel={`${t('voiceAgents.wizard.video.openForStepPrefix')} ${stepDisplayName} ${t('voiceAgents.wizard.video.openForStepSuffix')}`}
        style={styles.videoPlaceholder}
      >
        <Heading4 style={styles.videoPlaceholderTitle}>{stepDisplayName}</Heading4>
        <Caption style={styles.videoPlaceholderHint}>
          {t('voiceAgents.wizard.video.tapToOpen')}
        </Caption>
      </Pressable>
    );
  }

  return (
    <View
      style={styles.videoPlaceholder}
      accessible
      accessibilityRole="image"
      accessibilityLabel={`${t('voiceAgents.wizard.video.placeholderA11y')} ${stepDisplayName}`}
    >
      <Heading4 style={styles.videoPlaceholderTitle}>{stepDisplayName}</Heading4>
      <Caption style={styles.videoPlaceholderHint}>
        {t('voiceAgents.wizard.video.comingSoon')}
      </Caption>
    </View>
  );
}

function LabeledInput({
  label,
  accessibilityLabel,
  ...props
}: {
  label: string;
  accessibilityLabel: string;
} & React.ComponentProps<typeof TextInput>) {
  return (
    <VStack spacing="xs">
      <Label style={styles.inputLabel}>{label}</Label>
      <FormInput
        style={[styles.input, props.multiline && styles.inputMultiline]}
        accessible
        accessibilityLabel={accessibilityLabel}
        {...props}
      />
    </VStack>
  );
}

function SelectField({
  label,
  value,
  options,
  onSelect,
  accessibilityLabel,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onSelect: (v: string) => void;
  accessibilityLabel: string;
}) {
  return (
    <VStack spacing="xs">
      <Label style={styles.inputLabel}>{label}</Label>
      <View
        style={styles.chipWrap}
        accessible
        accessibilityLabel={accessibilityLabel}
        accessibilityRole="radiogroup"
      >
        {options.map((opt) => (
          <Button
            key={opt.value}
            variant={value === opt.value ? 'primary' : 'outline'}
            size="sm"
            onPress={() => onSelect(opt.value)}
            accessibilityRole="radio"
            accessibilityState={{ checked: value === opt.value }}
            accessibilityLabel={opt.label}
          >
            {opt.label}
          </Button>
        ))}
      </View>
    </VStack>
  );
}

function ToggleRow({
  label,
  value,
  onValueChange,
  accessibilityLabel,
}: {
  label: string;
  value: boolean;
  onValueChange: (v: boolean) => void;
  accessibilityLabel: string;
}) {
  return (
    <HStack style={styles.toggleRow} spacing="md">
      <Body style={[styles.toggleLabel, { flex: 1 }]}>{label}</Body>
      <Switch
        value={value}
        onValueChange={onValueChange}
        accessible
        accessibilityLabel={accessibilityLabel}
        accessibilityRole="switch"
        accessibilityState={{ checked: value }}
        trackColor={{
          false: colors.background.elevated,
          true: colors.primary.main,
        }}
        thumbColor={colors.foreground}
      />
    </HStack>
  );
}

function ReviewRow({
  label,
  value,
  onEdit,
}: {
  label: string;
  value: string;
  onEdit?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <HStack spacing="sm" style={styles.reviewRow}>
      <Caption style={styles.reviewLabel}>{label}</Caption>
      <Body style={styles.reviewValue} numberOfLines={2}>
        {value || '—'}
      </Body>
      {onEdit ? (
        <Button
          variant="ghost"
          size="sm"
          onPress={onEdit}
          accessibilityLabel={`${t('voiceAgents.wizard.review.editPrefix')} ${label}`}
          style={styles.reviewEditBtn}
        >
          {t('voiceAgents.wizard.review.edit')}
        </Button>
      ) : null}
    </HStack>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  stepHeader: {
    borderBottomWidth: 1,
    borderBottomColor: colors.border.muted,
    backgroundColor: colors.surface.navChrome,
  },
  stepHeaderRail: {
    width: '100%',
    maxWidth: 1240,
    alignSelf: 'center',
    paddingHorizontal: 24,
    paddingTop: 18,
    paddingBottom: 16,
    gap: 18,
  },
  stepHeaderRailWide: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 40,
  },
  stepCommandPanel: {
    gap: 12,
  },
  stepCommandPanelWide: {
    flex: 1,
    maxWidth: 780,
    alignItems: 'stretch',
  },
  stepList: {
    alignItems: 'center',
    paddingRight: 4,
  },
  stepListWide: {
    flexGrow: 1,
    justifyContent: 'flex-end',
  },
  stepConnector: {
    width: 26,
    height: 1,
    backgroundColor: colors.border.muted,
  },
  stepConnectorDone: {
    backgroundColor: colors.primary.borderActive,
  },
  stepButton: {
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepNumber: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.border.muted,
    backgroundColor: colors.background.input,
  },
  stepNumberActive: {
    borderColor: colors.primary.main,
    backgroundColor: colors.primary.muted,
  },
  stepNumberDone: {
    borderColor: colors.primary.main,
    backgroundColor: colors.primary.main,
  },
  stepNumberText: {
    color: colors.text.tertiary,
    fontFamily: 'monospace',
    fontSize: 10,
    fontVariant: ['tabular-nums'],
    letterSpacing: 0.4,
  },
  stepNumberTextActive: {
    color: colors.primary.neon,
  },
  stepTitleBlock: {
    gap: 4,
  },
  stepTitleBlockWide: {
    minWidth: 220,
  },
  stepCounter: {
    color: colors.primary[400],
    fontFamily: 'monospace',
    fontSize: 11,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
  },
  stepTitle: {
    color: colors.text.primary,
    fontSize: 24,
    lineHeight: 30,
  },
  scrollArea: {
    flex: 1,
  },
  scrollContent: {
    width: '100%',
    maxWidth: 1240,
    alignSelf: 'center',
    padding: 24,
    paddingBottom: 48,
    gap: 20,
  },
  stepContent: {
    gap: 20,
  },
  commandActions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 16,
    borderTopWidth: 1,
    borderTopColor: colors.border.subtle,
    paddingTop: 12,
  },
  nextStepContext: {
    flex: 1,
    alignItems: 'flex-end',
  },
  nextStepEyebrow: {
    color: colors.text.disabled,
    fontFamily: 'monospace',
    fontSize: 9,
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  nextStepName: {
    color: colors.text.secondary,
    fontSize: 12,
  },
  commandButtons: {
    alignItems: 'center',
  },
  commandButtonSecondary: {
    minWidth: 88,
  },
  commandButtonPrimary: {
    minWidth: 116,
  },
  commandButtonPrimaryDisabled: {
    backgroundColor: colors.background.elevated,
    borderWidth: 1,
    borderColor: colors.border.muted,
    opacity: 1,
  },
  error: {
    color: colors.status?.error?.main ?? '#f87171',
    marginTop: 4,
    lineHeight: 20,
  },
  hint: {
    color: colors.text.tertiary,
    lineHeight: 20,
  },
  voiceCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.background.elevated,
    backgroundColor: colors.background.input,
  },
  voiceCardSelected: {
    borderColor: colors.primary.main,
    backgroundColor: colors.primary.muted,
  },
  voiceSelectArea: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  voiceRadio: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: colors.border.default,
    alignItems: 'center',
    justifyContent: 'center',
  },
  voiceRadioSelected: {
    borderColor: colors.primary.main,
  },
  voiceRadioDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: colors.primary.main,
  },
  voiceInfo: {
    flex: 1,
  },
  voiceName: {
    color: colors.text.primary,
  },
  voiceCategory: {
    color: colors.text.tertiary,
  },
  chipWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  sectionNote: {
    color: colors.text.secondary,
    lineHeight: 22,
    maxWidth: 680,
  },
  useCaseIntro: {
    gap: 6,
  },
  sectionEyebrow: {
    color: colors.primary[400],
    fontFamily: 'monospace',
    fontSize: 11,
    letterSpacing: 1.4,
    textTransform: 'uppercase',
  },
  subHeading: {
    color: colors.text.primary,
    marginBottom: 4,
  },
  input: {
    backgroundColor: colors.background.input,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: colors.background.elevated,
    paddingHorizontal: 14,
    paddingVertical: 12,
    color: colors.text.primary,
    fontSize: 15,
    lineHeight: 22,
  },
  inputMultiline: {
    minHeight: 96,
    textAlignVertical: 'top',
  },
  inputLabel: {
    color: colors.text.secondary,
    fontSize: 13,
    fontWeight: '500',
    marginBottom: 2,
  },
  toggleRow: {
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.background.elevated,
  },
  toggleLabel: {
    color: colors.text.primary,
    fontSize: 15,
  },
  reviewRow: {
    alignItems: 'flex-start',
    borderBottomWidth: 1,
    borderBottomColor: colors.background.elevated,
    paddingBottom: 12,
    paddingTop: 4,
  },
  reviewLabel: {
    color: colors.text.tertiary,
    width: 130,
    fontSize: 13,
  },
  reviewValue: {
    color: colors.text.primary,
    flex: 1,
    fontSize: 14,
  },
  savedNote: {
    color: colors.primary[400] ?? colors.primary.main,
    fontSize: 12,
    marginTop: 8,
  },
  errorText: {
    color: colors.status.error.main,
    fontSize: 12,
    marginTop: 8,
  },
  reviewEditBtn: {
    minWidth: 0,
    paddingHorizontal: 8,
  },
  templateCard: {
    width: '100%',
    minHeight: 210,
    justifyContent: 'space-between',
    gap: 28,
    backgroundColor: colors.surface.row,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: colors.border.muted,
    padding: 20,
  },
  templateCardWide: {
    width: '48%',
    flexGrow: 1,
  },
  templateCardBlank: {
    borderStyle: 'dashed',
    backgroundColor: colors.background.input,
  },
  templateCardSelected: {
    borderColor: colors.primary.main,
    backgroundColor: colors.primary.faint,
  },
  templateCardPressed: {
    opacity: 0.82,
  },
  templateGrid: {
    gap: 14,
  },
  templateGridCompact: {
    flexDirection: 'column',
  },
  templateGridWide: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  templateCardTopRow: {
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  templateIcon: {
    width: 42,
    height: 42,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.border.muted,
    backgroundColor: colors.surface.iconWell,
  },
  templateIconSelected: {
    borderColor: colors.primary.borderActive,
    backgroundColor: colors.primary.muted,
  },
  templateRadio: {
    width: 18,
    height: 18,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1.5,
    borderColor: colors.border.strong,
  },
  templateRadioSelected: {
    borderColor: colors.primary.main,
  },
  templateRadioDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.primary.main,
  },
  templateCardCopy: {
    gap: 6,
  },
  templateCardTitle: {
    color: colors.text.primary,
    fontSize: 16,
    lineHeight: 22,
  },
  templateCardUseCase: {
    color: colors.primary[400],
    fontFamily: 'monospace',
    fontSize: 10,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },
  templateCardDescription: {
    color: colors.text.secondary,
    fontSize: 13,
    lineHeight: 19,
  },
  dayRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: colors.background.elevated,
  },
  dayLabel: {
    width: 40,
    color: colors.text.secondary,
    fontSize: 14,
  },
  dayTimeWrap: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  blackoutRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  blackoutChip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.background.elevated,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    marginBottom: 4,
  },
  videoPlaceholder: {
    backgroundColor: colors.background.secondary,
    borderRadius: 12,
    padding: 24,
    marginBottom: 16,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 120,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: colors.border.subtle,
  },
  videoEmbedWrap: {
    marginBottom: 16,
  },
  videoPlaceholderTitle: {
    marginBottom: 4,
  },
  videoPlaceholderHint: {
    color: colors.text.tertiary,
    textAlign: 'center',
  },
});
